import {closeDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {type ReactNode, useCallback, useState} from 'react';

import {isNil} from 'ramda';
import {isArray, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useDeleteServiceOrderItemsMutation,
  usePostServiceOrderCalculatePriceBulkMutation,
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartRequestMutation,
  usePostServiceItemsMaterialPartReturnMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  handleApiError,
  catchUnhandledDataGridActions,
  DmsDataGrid,
  queryParams,
  refreshDmsDatagrid,
  usePermissions,
} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps, useQueryState} from 'shared/web';

import type {
  ActionCallback,
  EagGridMaximization,
  GridDataChange,
  QueryFilterObject,
} from 'features/eag-grid';

import {JOB_ITEMS_GRID_CODE} from '../../../constants/jobItemsGridCode';
import {MATERIAL_TYPE} from '../../../constants/materialType';
import {openCreateCoreExchangeDialog} from '../../../utils/openCreateCoreExchangeDialog';
import {BulkAssignMechanicForm} from '../../BulkAssignMechanicForm';
import {BulkDiscountForm} from '../../BulkDiscountForm';
import {BulkVatChangeForm} from '../../BulkVatChangeForm';
import {BulkWorkTypeChangeForm} from '../../BulkWorkTypeChangeForm';
import {GoodwillForm} from '../../Goodwill/GoodwillForm';
import {IssueMaterialItems} from '../../IssueMaterialItems';
import {MoveRequestItem} from '../../MoveRequestItem/MoveRequestItem';
import {
  RequestEditDialogProps,
  RequestEditDialog,
  ReceiveJobItemRowData,
} from '../../RequestEditDialog';
import {WorkSplittingForm} from '../../WorkSplitting/WorkSplittingForm';
import {FindAlternativeDialog} from './FindAlternativeDialog';

type FindAlternativeDialogState = {
  serviceOrderJobItemId: string;
  itemName: string | null;
};

const PREDEFINED_COLUMNS = [
  'itemName',
  'itemType',
  'itemPriceType',
  'itemGoodwillRatio',
  'serviceOrderJobItemId',
  'serviceJobId',
  'warehouseArticleId',
  'assignMechanics',
  'itemUnitPriceWithVat',
  'itemUnitPriceWithoutVat',
  'itemUnit',
  'itemQuantity',
] as const;

interface JobItemsDataGridProps extends TestIdProps {
  maximization?: EagGridMaximization;
  /** Repeats the job's segment navigation and actions inside the maximized grid only. */
  maximizedSectionHeader?: ReactNode;
  serviceCaseId: string;
  orderId: string | Nullish;
  jobId: string | Nullish;
  datagridKey: string | Nullish;
  requestName: string | Nullish;
  authorizationProfileId: string | Nullish;
  /** Reports loaded rows + fetch state to the host (bulk work-item selection, T20-128140). */
  onGridDataChange?: (data: GridDataChange) => void;
}

export function JobItemsDataGrid(props: JobItemsDataGridProps) {
  const [canCreateCoreExchange] = usePermissions({permissionKeys: ['coreExchangeCreate']});
  const [, setServiceOrderReturnId] = useQueryState(queryParams.SERVICE_ORDER_RETURN_ID);
  const [, setServiceOrderReturnAuthorizationProfileId] = useQueryState(
    queryParams.SERVICE_ORDER_RETURN_AUTHORIZATION_PROFILE_ID
  );

  const [deleteServiceOrderItems] = useDeleteServiceOrderItemsMutation();
  const [postServiceOrderCalculatePriceBulk] = usePostServiceOrderCalculatePriceBulkMutation();
  const [postServiceItemsMaterialPartRequest] = usePostServiceItemsMaterialPartRequestMutation();
  const [postServiceItemsMaterialPartReturn] = usePostServiceItemsMaterialPartReturnMutation();
  const [postServiceItemsMaterialPartRequestCancellation] =
    usePostServiceItemsMaterialPartRequestCancellationMutation();

  const [editDialogProps, setEditDialogProps] = useState<RequestEditDialogProps | null>(null);
  const [findAlternativeState, setFindAlternativeState] =
    useState<FindAlternativeDialogState | null>(null);

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      serviceJobId: props.jobId ?? '',
      serviceOrderId: props.orderId ?? '',
      serviceCaseId: props.serviceCaseId,
    }),
    [props.jobId, props.orderId, props.serviceCaseId]
  );

  const actionCallback: ActionCallback = async ({
    rowId,
    actionKey,
    rowData,
    refreshData,
    queryId,
    deselectAll,
  }) => {
    const castedRowData = rowData as ReceiveJobItemRowData | ReceiveJobItemRowData[];

    const items = isArray(castedRowData)
      ? castedRowData.map((selectedRow) => ({
          serviceItemId: selectedRow.serviceOrderJobItemId.value,
          serviceJobId: props.jobId || '',
        }))
      : [
          {
            serviceItemId: castedRowData.serviceOrderJobItemId.value,
            serviceJobId: props.jobId || '',
          },
        ];

    const hasMaterialItem = (isArray(castedRowData) ? castedRowData : [castedRowData]).some(
      (row) => row.itemType.value.key === MATERIAL_TYPE
    );

    const hasSomeGoodwill =
      isArray(castedRowData) &&
      castedRowData.some(
        (data) =>
          isNotNilOrEmpty(data.itemGoodwillRatio.value) &&
          Number(data.itemGoodwillRatio.value?.label) !== 100
      );

    await match(actionKey)
      .with('getCurrentPrice', 'getCurrentPrices', () =>
        postServiceOrderCalculatePriceBulk({
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId ?? '',
          body: {serviceItems: items},
        })
          .unwrap()
          .then(() => {
            deselectAll();
            refreshData();
          })
          .catch(handleApiError)
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteServiceOrderItems({
              serviceOrderId: props.orderId ?? '',
              serviceCaseId: props.serviceCaseId,
              body: {items},
            })
              .unwrap()
              .then(() => {
                deselectAll();
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .with('edit', 'redirectDetail', () => {
        if (isArray(castedRowData) || isNil(castedRowData)) {
          return;
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
        };

        setEditDialogProps({
          gridCode: JOB_ITEMS_GRID_CODE,
          rowId: isArray(rowId) ? rowId[0] : rowId,
          dataQueryId: queryId,
          serviceCaseId: props.serviceCaseId,
          orderId: props.orderId ?? '',
          itemData: castedRowData,
          onClose: handleClose,
          'data-testid': props['data-testid'] as string,
        });
      })
      .with('move', () =>
        openDialog(
          <MoveRequestItem
            hasMaterialItem={hasMaterialItem}
            onClose={() => closeDialog('moveRequestItem')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={String(props.orderId)}
            hiddenServiceJob={props.jobId}
            items={items}
            onMoveItems={refreshData}
            data-testid={suffixTestId('moveItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.moveRequestItem'),
            id: 'moveRequestItem',
          }
        )
      )
      .with('goodwill', () =>
        openDialog(
          <GoodwillForm
            onClose={() => closeDialog('goodwill')}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={String(props.orderId)}
            requestName={props.requestName}
            items={items}
            onSaved={refreshData}
            isWithMultipleGoodwill={hasSomeGoodwill}
            data-testid={suffixTestId('goodwillDialog', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.goodwill'),
            id: 'goodwill',
          }
        )
      )
      .with('workSplitting', () =>
        openDialog(
          <WorkSplittingForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('workSplitting')}
            data-testid={suffixTestId('workSplitting', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.workSplitting'),
            id: 'workSplitting',
          }
        )
      )
      .with(
        'request',
        async () =>
          await postServiceItemsMaterialPartRequest({
            body: {serviceItems: items},
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.orderId ?? '',
          })
            .unwrap()
            .then(() => refreshData())
            .catch(handleApiError)
      )
      .with('issue', () => {
        const defaultMechanicId = isArray(castedRowData)
          ? castedRowData[0]?.assignMechanics?.value?.[0]?.key
          : castedRowData?.assignMechanics?.value?.[0]?.key;

        return openDialog(
          <IssueMaterialItems
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            defaultMechanicId={defaultMechanicId}
            onSubmit={() => refreshData()}
            onClose={() => closeDialog('issueMaterialItem')}
            data-testid={suffixTestId('issueMaterialItem', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.issueMaterialItem'),
            id: 'issueMaterialItem',
            size: 'small',
          }
        );
      })
      .with('assignMechanic', () =>
        openDialog(
          <BulkAssignMechanicForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('assignMechanic')}
            data-testid={suffixTestId('assignMechanic', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.assignMechanic'),
            id: 'assignMechanic',
            size: 'small',
          }
        )
      )
      .with('discount', () =>
        openDialog(
          <BulkDiscountForm
            serviceCaseId={props.serviceCaseId ?? ''}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('manualDiscount')}
            data-testid={suffixTestId('manualDiscount', props)}
          />,
          {
            title: i18n.t('entity.orderItem.labels.manualDiscount'),
            id: 'manualDiscount',
            size: 'small',
          }
        )
      )
      .with('workTypeChange', () =>
        openDialog(
          <BulkWorkTypeChangeForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('workTypeChange')}
            data-testid={suffixTestId('workTypeChange', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.workTypeChange'),
            id: 'workTypeChange',
            size: 'small',
          }
        )
      )
      .with('vatChange', () =>
        openDialog(
          <BulkVatChangeForm
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            items={items}
            onSubmit={refreshData}
            onClose={() => closeDialog('vatChange')}
            data-testid={suffixTestId('vatChange', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.vatChange'),
            id: 'vatChange',
            size: 'small',
          }
        )
      )
      .with('return', () => {
        if (isNil(props.orderId)) {
          throw new Error('serviceOrderId is not defined');
        }

        postServiceItemsMaterialPartReturn({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId,
        })
          .unwrap()
          .then((result) => {
            if (isNotNil(result?.serviceOrderReturnId)) {
              setServiceOrderReturnId(result?.serviceOrderReturnId);
            }
            if (isNotNil(result?.authorizationProfileId)) {
              setServiceOrderReturnAuthorizationProfileId(result?.authorizationProfileId);
            }
          })
          .then(deselectAll)
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('requestCancellation', () =>
        postServiceItemsMaterialPartRequestCancellation({
          body: {serviceItems: items},
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.orderId ?? '',
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError)
      )
      .with('findAlternative', () => {
        if (isArray(castedRowData) || isNil(castedRowData)) {
          return;
        }

        setFindAlternativeState({
          serviceOrderJobItemId: castedRowData.serviceOrderJobItemId.value,
          itemName: castedRowData.itemName?.value ?? null,
        });
      })
      .with('createCoreExchange', () =>
        openCreateCoreExchangeDialog(
          castedRowData,
          props.serviceCaseId,
          props.orderId,
          canCreateCoreExchange,
          props
        )
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Show when={isNotNil(editDialogProps)}>
        <RequestEditDialog {...(editDialogProps as RequestEditDialogProps)} />
      </Show>
      <Show when={findAlternativeState}>
        {(findAlternativeState) => (
          <FindAlternativeDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.orderId ?? ''}
            serviceJobId={props.jobId ?? ''}
            serviceOrderJobItemId={findAlternativeState.serviceOrderJobItemId}
            authorizationProfileId={props.authorizationProfileId ?? ''}
            itemName={findAlternativeState.itemName}
            onClose={() => setFindAlternativeState(null)}
            onReplaced={() => refreshDmsDatagrid(JOB_ITEMS_GRID_CODE)}
            data-testid={suffixTestId('findAlternative', props)}
          />
        )}
      </Show>
      <DmsDataGrid
        maximization={props.maximization}
        maximizedSectionHeader={props.maximizedSectionHeader}
        autoHeight
        predefinedColumns={PREDEFINED_COLUMNS}
        gridCode={JOB_ITEMS_GRID_CODE}
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        onGridDataChange={props.onGridDataChange}
        data-testid={suffixTestId('dataGrid', props)}
      />
    </>
  );
}
